import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ExternalLink, ArrowRight, Circle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Explorer = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    ledgerIndex: 0,
    txCount: 0,
    tps: 0,
  });
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchXRPLData = useCallback(async () => {
    try {
      const ledgerResponse = await fetch('https://xrplcluster.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method: 'ledger',
          params: [{ ledger_index: 'validated', transactions: true, expand: true }]
        })
      });
      
      const ledgerData = await ledgerResponse.json();
      
      if (ledgerData.result && ledgerData.result.ledger) {
        const ledger = ledgerData.result.ledger;
        const txs = ledger.transactions || [];
        
        const processedTxs = txs.slice(0, 12).map((tx, index) => {
          const txData = tx.tx || tx;
          return {
            id: txData.hash || `tx-${index}`,
            hash: txData.hash || 'N/A',
            type: txData.TransactionType || 'Unknown',
            account: txData.Account || 'Unknown',
            destination: txData.Destination || null,
            amount: txData.Amount ? (typeof txData.Amount === 'string' ? (parseInt(txData.Amount) / 1000000).toFixed(2) : 'Token') : '0',
            fee: txData.Fee ? (parseInt(txData.Fee) / 1000000).toFixed(6) : '0',
            timestamp: ledger.close_time ? new Date((ledger.close_time + 946684800) * 1000).toLocaleTimeString() : 'N/A',
            status: tx.meta?.TransactionResult === 'tesSUCCESS' ? 'success' : 'pending',
          };
        });

        setTransactions(processedTxs);
        setStats({
          ledgerIndex: ledger.ledger_index || 0,
          txCount: txs.length,
          tps: (txs.length / 4).toFixed(1),
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching XRPL data:', error);
      generateMockData();
      setLoading(false);
    }
  }, []);

  const generateMockData = () => {
    const mockTxs = Array.from({ length: 12 }, (_, i) => ({
      id: `mock-${i}`,
      hash: `${Math.random().toString(36).substring(2, 10).toUpperCase()}${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
      type: ['Payment', 'OfferCreate', 'TrustSet'][Math.floor(Math.random() * 3)],
      account: `r${Math.random().toString(36).substring(2, 15)}`,
      destination: Math.random() > 0.3 ? `r${Math.random().toString(36).substring(2, 15)}` : null,
      amount: (Math.random() * 10000).toFixed(2),
      fee: '0.000012',
      timestamp: new Date().toLocaleTimeString(),
      status: Math.random() > 0.1 ? 'success' : 'pending',
    }));
    
    setTransactions(mockTxs);
    setStats({
      ledgerIndex: 85000000 + Math.floor(Math.random() * 1000000),
      txCount: Math.floor(Math.random() * 50) + 10,
      tps: (Math.random() * 20 + 5).toFixed(1),
    });
  };

  useEffect(() => {
    fetchXRPLData();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchXRPLData, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchXRPLData]);

  const truncateHash = (hash) => {
    if (!hash || hash === 'N/A') return 'N/A';
    return `${hash.substring(0, 6)}...${hash.substring(hash.length - 4)}`;
  };

  const truncateAddress = (addr) => {
    if (!addr) return '-';
    return `${addr.substring(0, 8)}...${addr.substring(addr.length - 4)}`;
  };

  return (
    <div className="min-h-screen bg-[#0b0014]">
      {/* Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0b0014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_stock-mirrors/artifacts/gdgmfuzc_2.%20Logo%202.png" 
                alt="Stocks Protocol" 
                className="h-40 w-auto object-contain"
              />
            </Link>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Circle size={8} className="text-green-400 fill-green-400 animate-pulse" />
                <span className="text-sm text-gray-400">Live</span>
              </div>
              <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      <main className="pt-28 pb-20">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-3xl font-semibold text-white mb-2">
              Explorer
            </h1>
            <p className="text-gray-500">
              Real-time XRPL network activity
            </p>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Ledger</p>
              <p className="text-xl font-mono text-white">{stats.ledgerIndex.toLocaleString()}</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Transactions</p>
              <p className="text-xl font-mono text-white">{stats.txCount}</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">TPS</p>
              <p className="text-xl font-mono text-white">{stats.tps}</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Finality</p>
              <p className="text-xl font-mono text-white">~4s</p>
            </div>
          </div>

          {/* Transactions */}
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Recent Transactions</h2>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-8 h-4 rounded-full transition-colors ${autoRefresh ? 'bg-purple-500' : 'bg-white/10'}`}>
                  <div className={`w-3 h-3 rounded-full bg-white mt-0.5 transition-transform ${autoRefresh ? 'translate-x-4.5 ml-4' : 'ml-0.5'}`} />
                </div>
                <span className="text-xs text-gray-500">Auto</span>
              </label>
              <button
                onClick={fetchXRPLData}
                disabled={loading}
                className="p-2 text-gray-500 hover:text-white transition-colors"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Transaction List */}
          <div className="space-y-2">
            {loading ? (
              <div className="py-20 text-center">
                <RefreshCw className="w-6 h-6 text-purple-400 animate-spin mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Loading...</p>
              </div>
            ) : (
              transactions.map((tx, index) => (
                <div 
                  key={tx.id}
                  className="group flex items-center gap-4 p-4 rounded-lg bg-white/[0.01] hover:bg-white/[0.03] border border-transparent hover:border-white/5 transition-all"
                >
                  {/* Status Indicator */}
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tx.status === 'success' ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
                  
                  {/* Hash */}
                  <a 
                    href={`https://xrpscan.com/tx/${tx.hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sm text-gray-400 hover:text-purple-400 transition-colors w-28 flex-shrink-0"
                  >
                    {truncateHash(tx.hash)}
                  </a>
                  
                  {/* Type Badge */}
                  <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${
                    tx.type === 'Payment' ? 'text-green-400 bg-green-400/10' :
                    tx.type === 'OfferCreate' ? 'text-blue-400 bg-blue-400/10' :
                    'text-purple-400 bg-purple-400/10'
                  }`}>
                    {tx.type}
                  </span>
                  
                  {/* From -> To */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="font-mono text-sm text-gray-500 truncate">{truncateAddress(tx.account)}</span>
                    {tx.destination && (
                      <>
                        <ArrowRight size={12} className="text-gray-600 flex-shrink-0" />
                        <span className="font-mono text-sm text-gray-500 truncate">{truncateAddress(tx.destination)}</span>
                      </>
                    )}
                  </div>
                  
                  {/* Amount */}
                  <div className="text-right flex-shrink-0 w-32">
                    <span className="text-sm text-white">{tx.amount}</span>
                    <span className="text-xs text-gray-600 ml-1">XRP</span>
                  </div>
                  
                  {/* Time */}
                  <span className="text-xs text-gray-600 w-20 text-right flex-shrink-0">{tx.timestamp}</span>
                  
                  {/* External Link */}
                  <a 
                    href={`https://xrpscan.com/tx/${tx.hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-purple-400 transition-all"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
              ))
            )}
          </div>

          {/* Footer Info */}
          <div className="mt-16 pt-8 border-t border-white/5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                Data sourced from XRP Ledger mainnet
              </p>
              <div className="flex items-center gap-6">
                <a 
                  href="https://xrpl.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-500 hover:text-purple-400 transition-colors flex items-center gap-1"
                >
                  XRPL.org <ExternalLink size={12} />
                </a>
                <a 
                  href="https://xrpscan.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-500 hover:text-purple-400 transition-colors flex items-center gap-1"
                >
                  XRPScan <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Explorer;
